import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

// Actions
import { addCustomYieldHolding, setHoldingToReplace } from '@/redux/modules/edit-portfolio';
import { setFocusedQuickSearchEditPorfolio } from '@/redux/modules/quick-search';

// Components
import { DatePicker, Input, SelectInput, SelectOption, ToggleSwitch, UnitInput } from '@/views/components/inputs';
import { DefaultModal } from '@/views/components/modals';
import { Tooltip } from '@/views/components/tooltips';
import { withErrorBoundary } from '@/views/components/errors';

// Libraries
import classNames from 'classnames';
import moment from 'moment';

// Types
import CompliantInstrument from '@/types/compliant-instrument';
import CustomYieldParams from '@/types/custom-yield-params';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import PortfolioHolding from '@/types/portfolio-holding';
import QuickAddGICSimulator from '@/types/quick-add-gic-simulator';
import QuickAddHISASimulator from '@/types/quick-add-hisa-simulator';
import QuickAddItems from '@/types/quick-add-items';
import QuickAddUIType from '@/types/quick-add-u-i-type';
import ShelvableInstrument from '@/types/shelvable-instrument';
import User from '@/types/user';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { CURRENCY } from '@/utils/currency';
import { formatMessage, FormattedMessage, LOCALE } from '@/utils/i18n';
import { LABEL_FORMATTERS } from '@/utils/charts';
import { MODAL_SIZES, YieldParamsType } from '@/utils/constants';

enum TERM_UNITS {
  Months = 'months',
  Years = 'years',
}

type DispatchProps = {
  addCustomYieldHolding: (
    config: CustomYieldParams,
    shelfInfo: ShelvableInstrument & CompliantInstrument,
    currency: string,
    holdingToReplace?: PortfolioHolding,
    showReplacedFirst?: boolean,
  ) => void;
  setFocusedQuickSearchEditPorfolio: (focused?: boolean) => void;
  setHoldingToReplace: (holdingToReplace?: PortfolioHolding) => void;
};

type StateProps = {
  holdingToReplace?: PortfolioHolding;
  locale: Locale;
  quickAddItems: QuickAddItems[];
  user?: Nullable<User>;
};

type Props = DispatchProps &
  StateProps & {
    show: boolean;
    simulatorTitle: string;
    simulatorType: YieldParamsType;
    onCancel: () => void;
    onConfirm: () => void;
  };

const SimulatorModal = (props: Props) => {
  const {
    holdingToReplace,
    locale,
    quickAddItems,
    show,
    simulatorTitle,
    simulatorType,
    user,
    addCustomYieldHolding,
    onCancel,
    onConfirm,
    setFocusedQuickSearchEditPorfolio,
    setHoldingToReplace,
  } = props;

  const DEFAULT_OPTIONS = {
    automaticRenewal: true,
    name: simulatorTitle,
    rate: '1.00',
    startDate: null,
    term: '1',
    termUnits: TERM_UNITS.Years,
  };
  const isGicSimulator = simulatorType === YieldParamsType.GIC;
  const isHisaSimulator = simulatorType === YieldParamsType.HISA;

  const [automaticRenewal, setAutomaticRenewal] = useState<boolean>(DEFAULT_OPTIONS.automaticRenewal);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [name, setName] = useState<string>(DEFAULT_OPTIONS.name);
  const [nameError, setNameError] = useState<boolean>(false);
  const [rate, setRate] = useState<Nullable<string>>(DEFAULT_OPTIONS.rate);
  const [rateError, setRateError] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Nullable<Date>>(DEFAULT_OPTIONS.startDate);
  const [term, setTerm] = useState<Nullable<string>>(DEFAULT_OPTIONS.term);
  const [termError, setTermError] = useState<boolean>(false);
  const [termUnits, setTermUnits] = useState<TERM_UNITS>(DEFAULT_OPTIONS.termUnits);

  const clearForm = useCallback(() => {
    setAutomaticRenewal(DEFAULT_OPTIONS.automaticRenewal);
    setName(DEFAULT_OPTIONS.name);
    setRate(DEFAULT_OPTIONS.rate);
    setStartDate(DEFAULT_OPTIONS.startDate);
    setTerm(DEFAULT_OPTIONS.term);
    setTermUnits(DEFAULT_OPTIONS.termUnits);

    if (isSubmitting) {
      setIsSubmitting(false);
    }
  }, [
    DEFAULT_OPTIONS.automaticRenewal,
    DEFAULT_OPTIONS.name,
    DEFAULT_OPTIONS.rate,
    DEFAULT_OPTIONS.startDate,
    DEFAULT_OPTIONS.term,
    DEFAULT_OPTIONS.termUnits,
    isSubmitting,
  ]);

  useEffect(() => () => clearForm(), [clearForm]);

  const formatNavigationLabel = ({ date }: { date: Date }) => {
    const { months } = LABEL_FORMATTERS.lang(props.locale || LOCALE.EN_CA);
    return (
      <p className="formatted-label">
        <strong>{months[date.getMonth()]}</strong>
        <span>,</span> {date.getFullYear()}
      </p>
    );
  };

  const formatShortWeekday: (locale: string | undefined, date: Date) => string = (locale, date) => {
    if (!date) return '';
    const { shortWeekdays } = LABEL_FORMATTERS.lang(props.locale || LOCALE.EN_CA);
    return shortWeekdays[date.getDay()];
  };

  const handleOnChangeName = (value: string) => {
    setNameError(!value);
    setName(value);
  };

  const handleOnChangeRate = (value: string) => {
    setRateError(!value);
    setRate(value);
  };

  const handleOnChangeTerm = (value: string) => {
    setTermError(!value);
    setTerm(value);
  };

  const getBody = () => (
    <>
      <div className="row">
        <div className="col-12">
          <label className="label-v2" htmlFor="name">
            <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.Name" />
          </label>
        </div>

        <div className="col-12">
          <Input
            className={classNames('input-v2', { 'field-error': nameError })}
            controlled={false}
            data-testid="simulator-field-name"
            id="name"
            name="name"
            onValueChange={handleOnChangeName}
            value={name}
          />
          {nameError && (
            <div className="field-error-label">
              <i className="icon-exclamation-outline" />
              <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.NameError" />
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className={classNames({ 'col-4': isGicSimulator, 'col-6': isHisaSimulator })}>
          <label className="label-v2" htmlFor="rate">
            <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.Rate" />
          </label>

          <UnitInput
            className={classNames('unit-input-v2', { 'field-error': rateError })}
            controlled={false}
            data-testid="simulator-field-rate"
            id="rate"
            min={0}
            name="rate"
            onValueChange={handleOnChangeRate}
            type="number"
            unitSymbol="%"
            value={rate === null ? '' : rate}
          />
          {rateError && (
            <div className="field-error-label">
              <i className="icon-exclamation-outline" />
              <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.RateError" />
            </div>
          )}
        </div>

        {isGicSimulator && renderTermAndFrequencyFields()}
      </div>

      {isGicSimulator && renderStartDateAndRenewalFields()}
    </>
  );

  const getSimulatorItem = () => {
    if (isGicSimulator) {
      return quickAddItems.find(item => item.uiType === QuickAddUIType.GICSimulator) as QuickAddGICSimulator;
    }

    if (isHisaSimulator) {
      return quickAddItems.find(item => item.uiType === QuickAddUIType.HISASimulator) as QuickAddHISASimulator;
    }

    return false;
  };

  const handleOnConfirmSimulation = () => {
    const currency: string = user?.appConfig.localCurrency || CURRENCY.CAD;
    const hasEmptyName = !name || name.trim() === '';
    const hasEmptyRate = !rate || rate.trim() === '';
    const hasEmptyTerm = !term || term.trim() === '';
    const simulator = getSimulatorItem();

    setIsSubmitting(true);

    if (!simulator || hasEmptyName || hasEmptyRate || hasEmptyTerm) {
      setIsSubmitting(false);
      return;
    }

    const config: CustomYieldParams = {
      automaticRenewal,
      benchmarkLipperId: simulator.options?.benchmarkLipperId,
      frequency: 'daily',
      name,
      rate: Number(rate),
      startDate: startDate || moment().utc().startOf('year').startOf('day').subtract('10', 'years').startOf('day').toDate(),
      term: Number(term),
      termUnits,
      type: simulatorType,
    };

    const shelfInfo = {
      complianceComparisons: simulator.complianceComparisons,
      filteredShelves: simulator.filteredShelves,
    };

    addCustomYieldHolding(config, shelfInfo, currency, holdingToReplace, true);
    onConfirm();
    clearForm();

    if (holdingToReplace) {
      setHoldingToReplace(undefined);
      setFocusedQuickSearchEditPorfolio(false);
    }
  };

  const renderStartDateAndRenewalFields = () => (
    <>
      <div className="row">
        <div className="col-8">
          <label className="label-v2" htmlFor="start-date">
            <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.StartDate" />
            <Tooltip className="custom-tooltip-v2" message={formatMessage('Search.QuickSearchV2.SimulatorModal.StartDateTooltip')} place="bottom">
              <i className="icon-info-outline-new" />
            </Tooltip>
          </label>

          <DatePicker
            allowPrevNext2
            calendarType="US"
            className="date-picker-v2"
            customFormat="MMMM DD, YYYY"
            customPlaceholder={formatMessage('Search.QuickSearchV2.SimulatorModal.StartDatePlaceholder')}
            defaultDate={startDate}
            formatShortWeekday={formatShortWeekday}
            icon="icon-calendar-new"
            locale={locale}
            navigationLabel={formatNavigationLabel}
            onDateChange={(date: Date) => setStartDate(date)}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12 automatic-renewal">
          <ToggleSwitch className="toggle-switch-v2" onChange={setAutomaticRenewal} checked={automaticRenewal} />
          <span className="field-label">
            <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.AutomaticRenewal" />
          </span>
        </div>
      </div>
    </>
  );

  const renderTermAndFrequencyFields = () => (
    <>
      <div className="col-4">
        <label className="label-v2" htmlFor="term">
          <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.Term" />
        </label>

        <UnitInput
          className={classNames('unit-input-v2', { 'field-error': termError })}
          controlled={false}
          data-testid="simulator-field-term"
          id="term"
          min={0}
          name="term"
          onValueChange={handleOnChangeTerm}
          type="number"
          value={term === null ? '' : term}
        />
        {termError && (
          <div className="field-error-label">
            <i className="icon-exclamation-outline" />
            <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.TermError" />
          </div>
        )}
      </div>
      <div className="col-4">
        <label className="label-v2" htmlFor="frequency">
          <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.Frequency" />
        </label>

        <SelectInput className="select-input-v2" onValueChange={setTermUnits} selected={termUnits}>
          <SelectOption key={TERM_UNITS.Months} value={TERM_UNITS.Months}>
            <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.Months" />
          </SelectOption>
          <SelectOption key={TERM_UNITS.Years} value={TERM_UNITS.Years}>
            <FormattedMessage id="Search.QuickSearchV2.SimulatorModal.Years" />
          </SelectOption>
        </SelectInput>
      </div>
    </>
  );

  return (
    <DefaultModal
      backdropClassName="modal-v2-backdrop"
      body={getBody()}
      modalClassName="simulator-modal modal-v2"
      confirmLabel={formatMessage('Search.QuickSearchV2.AddToPortfolio')}
      isSubmitting={isSubmitting}
      isV2
      onCancel={onCancel}
      onConfirm={handleOnConfirmSimulation}
      show={show}
      size={MODAL_SIZES.SM}
      title={simulatorTitle}
    />
  );
};

const mapStateToProps = (state: ReduxState) => ({
  holdingToReplace: state.editPortfolio.holdingToReplace,
  locale: state.intl.locale,
  quickAddItems: state.advancedSearch.quickAddItems,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  addCustomYieldHolding,
  setFocusedQuickSearchEditPorfolio,
  setHoldingToReplace,
})(withErrorBoundary(SimulatorModal));
